import { DataProvider } from "@refinedev/core";
import { authFetch } from '../services/authFetch';
import queryString from "query-string";

// Get reliable API URL
function getReliableApiUrl(): string {
  const hostname = window.location.hostname;
  
  // For development (localhost), always use localhost:8000 regardless of subdomain
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    const protocol = window.location.protocol;
    return `${protocol}//localhost:8000`;
  }
  
  // For production, use same origin (nginx proxies /v1/ to backend)
  return window.location.origin;
}

const API_URL = getReliableApiUrl();

// Helper function to get current tenant
const getCurrentTenant = () => {
  // In isolated DB/subdomain architecture, tenant is determined by:
  // 1. Server configuration (different servers for different tenants)
  // 2. Database isolation (each server connects to its own tenant DB)
  // 3. Subdomain (handled at routing level)
  const subdomain = window.location.hostname.split('.')[0];
  return { subdomain };
};

// Helper function to get auth headers with JWT service
const getAuthHeaders = async () => {
  // For localhost development, skip authentication to avoid token issues
  const isLocalhost = window.location.hostname.includes('localhost') || window.location.hostname.includes('127.0.0.1');
  
  if (isLocalhost) {
    console.log('📡 DataProvider: Localhost development mode - skipping authentication');
    return {
      "Content-Type": "application/json",
    };
  }
  
  const { jwtAuthService } = await import('../services/jwtAuthService');
  const token = await jwtAuthService.getAccessToken();
  const tenant = getCurrentTenant();
  
  console.log('📡 DataProvider getAuthHeaders:', {
    hasToken: !!token,
    tokenLength: token?.length || 0,
    tenantSubdomain: tenant.subdomain
  });
  
  if (!token) {
    console.warn('⚠️ No authentication token found, skipping authorization header');
  }
  
  return {
    "Content-Type": "application/json",
    ...(token && { "Authorization": `Bearer ${token}` }),
  };
};

// Helper function to build filters
const buildFilters = (filters: any[] = []) => {
  const params: Record<string, any> = {};
  
  filters.forEach((filter) => {
    if (filter.operator === "eq") {
      params[filter.field] = filter.value;
    } else if (filter.operator === "contains") {
      params[`${filter.field}__icontains`] = filter.value;
    } else if (filter.operator === "gte") {
      params[`${filter.field}__gte`] = filter.value;
    } else if (filter.operator === "lte") {
      params[`${filter.field}__lte`] = filter.value;
    }
  });
  
  return params;
};

// Helper function to build sorters
const buildSorters = (sorters: any[] = []) => {
  if (sorters.length === 0) return {};
  
  const ordering = sorters.map((sorter) => {
    const { field, order } = sorter;
    return order === "desc" ? `-${field}` : field;
  }).join(",");
  
  return { ordering };
};

// Helper function to build API URL for resource
const buildResourceUrl = (apiUrl: string, resource: string): string => {
  // For billing resources, use relative URLs to go through proxy
  if (resource === "billing-entities") {
    return `/v1/billing/billing-entities/`;
  }
  if (resource === "invoices") {
    return `/v1/billing/invoices/`;
  }
  if (resource === "payments") {
    return `/v1/billing/payments/`;
  }
  if (resource === "billing/dashboard") {
    return `/v1/billing/dashboard/summary/`;
  }
  
  // Tax management resources (enterprise billing)
  if (resource === "tax-types" || resource === "tenant-tax-types") {
    return `/v1/enterprise-billing/tax-types/`;
  }
  if (resource === "tax-exemptions" || resource === "tenant-tax-exemptions") {
    return `/v1/enterprise-billing/tax-exemptions/`;
  }
  if (resource === "tax-configurations" || resource === "tenant-tax-configurations") {
    return `/v1/enterprise-billing/tax-configurations/`;
  }
  if (resource === "tax-calculations" || resource === "tenant-tax-calculations") {
    return `/v1/enterprise-billing/tax-calculations/`;
  }
  
  // For other resources, use full API URL
  const baseApiUrl = apiUrl || "https://labboxx.com/api";
  
  // Handle special cases where the resource name differs from the API endpoint
  if (resource === "orders") {
    return `${baseApiUrl}/v1/orders/orders/`;
  }
  if (resource === "order-items") {
    return `${baseApiUrl}/v1/orders/order-items/`;
  }
  
  // Core management resources
  if (resource === "companies") {
    return `${baseApiUrl}/v1/core/companies/`;
  }
  if (resource === "branches") {
    return `${baseApiUrl}/v1/core/branches/`;
  }
  if (resource === "addresses") {
    return `${baseApiUrl}/v1/core/addresses/`;
  }
  if (resource === "notification-templates") {
    return `${baseApiUrl}/v1/core/api/notifications/templates/`;
  }
  
  // Inventory management resources
  if (resource === "inventory/items" || resource === "inventory-items") {
    return `${baseApiUrl}/v1/inventory/items/`;
  }
  if (resource === "inventory/categories" || resource === "inventory-categories") {
    return `${baseApiUrl}/v1/inventory/categories/`;
  }
  if (resource === "inventory/suppliers" || resource === "inventory-suppliers") {
    return `${baseApiUrl}/v1/inventory/suppliers/`;
  }
  if (resource === "inventory/movements" || resource === "inventory-movements") {
    return `${baseApiUrl}/v1/inventory/movements/`;
  }
  
  // Device management resources
  if (resource === "devices/connectors") {
    return `${baseApiUrl}/v1/devices/connectors/`;
  }
  if (resource === "devices/analyzers") {
    return `${baseApiUrl}/v1/devices/analyzers/`;
  }
  if (resource === "devices/imaging") {
    return `${baseApiUrl}/v1/devices/imaging/`;
  }
  if (resource === "devices/imaging-studies") {
    return `${baseApiUrl}/v1/devices/imaging-studies/`;
  }
  if (resource === "devices/monitoring") {
    return `${baseApiUrl}/v1/devices/monitoring/`;
  }

  // Results management resources
  if (resource === "results-worklist-items") {
    return `${baseApiUrl}/v1/results/worklist-items/`;
  }
  if (resource === "results-worklists") {
    return `${baseApiUrl}/v1/results/worklists/`;
  }
  if (resource === "results-critical-alerts") {
    return `${baseApiUrl}/v1/results/critical-alerts/`;
  }

  // Tenant User Management resources
  if (resource === "tenant-users" || resource === "users") {
    return `${baseApiUrl}/v1/tenant-users/api/users/`;
  }
  if (resource === "tenant-roles" || resource === "roles") {
    return `${baseApiUrl}/v1/tenant-users/api/roles/`;
  }
  if (resource === "tenant-permissions" || resource === "permissions") {
    return `${baseApiUrl}/v1/tenant-users/api/permissions/`;
  }
  if (resource === "tenant-user-sessions" || resource === "user-sessions") {
    return `${baseApiUrl}/v1/tenant-users/api/sessions/`;
  }
  if (resource === "tenant-user-profiles" || resource === "user-profiles") {
    return `${baseApiUrl}/v1/tenant-users/api/profiles/`;
  }
  
  return `${baseApiUrl}/v1/${resource}/`;
};

export const dataProvider = (apiUrl: string = API_URL): DataProvider => ({
  getApiUrl: () => apiUrl,

  getList: async ({ resource, pagination, filters, sorters }) => {
    const url = buildResourceUrl(apiUrl, resource);

    const query = {
      ...buildFilters(filters),
      ...buildSorters(sorters),
      ...(pagination && {
        limit: pagination.pageSize,
        // Refine v6 uses currentPage instead of current
        offset: ((pagination as any).currentPage
          ? ((pagination as any).currentPage - 1)
          : ((pagination as any).current
            ? ((pagination as any).current - 1)
            : 0)) * (pagination.pageSize || 0),
      }),
    };

    try {
      const headers = await getAuthHeaders();
      const response = await authFetch(`${url}?${queryString.stringify(query)}`, {
        headers,
        credentials: 'include',
      });

      // Handle authentication errors - JWT service will handle token cleanup
      if (response.status === 401) {
        throw new Error("Authentication required");
      }

      // Check content type before parsing
      const contentType = response.headers.get("content-type");
      console.log(`Content-Type for ${resource}:`, contentType, 'Status:', response.status);
      if (!contentType || !contentType.includes("application/json")) {
        console.error(`Non-JSON response for ${resource}:`, response.status, response.statusText);
        return {
          data: [],
          total: 0,
        };
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: `HTTP ${response.status}` }));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      return {
        data: data.results || data.data || data,
        total: data.count || data.total || data.length || 0,
      };
    } catch (error) {
      console.error(`Error fetching ${resource}:`, error);
      // Return empty data instead of throwing to prevent app crash
      return {
        data: [],
        total: 0,
      };
    }
  },

  getOne: async ({ resource, id }) => {
    const baseUrl = buildResourceUrl(apiUrl, resource);
    const url = `${baseUrl}${id}/`;

    const headers = await getAuthHeaders();
    const response = await authFetch(url, {
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    return {
      data,
    };
  },

  create: async ({ resource, variables }) => {
    const url = buildResourceUrl(apiUrl, resource);

    const headers = await getAuthHeaders();
    const response = await authFetch(url, {
      method: "POST",
      headers: {
        ...headers,
        "Idempotency-Key": crypto.randomUUID(),
      },
      credentials: 'include',
      body: JSON.stringify(variables),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    return {
      data,
    };
  },

  update: async ({ resource, id, variables }) => {
    const baseUrl = buildResourceUrl(apiUrl, resource);
    const url = `${baseUrl}${id}/`;

    const headers = await getAuthHeaders();
    const response = await authFetch(url, {
      method: "PATCH",
      headers: {
        ...headers,
        "Idempotency-Key": crypto.randomUUID(),
      },
      credentials: 'include',
      body: JSON.stringify(variables),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    return {
      data,
    };
  },

  deleteOne: async ({ resource, id }) => {
    const baseUrl = buildResourceUrl(apiUrl, resource);
    const url = `${baseUrl}${id}/`;

    const headers = await getAuthHeaders();
    const response = await authFetch(url, {
      method: "DELETE",
      headers: headers,
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Return the deleted record or minimal data
    return {
      data: { id } as any,
    };
  },

  getMany: async ({ resource, ids }) => {
    const query = queryString.stringify({ id__in: ids });
    const baseUrl = buildResourceUrl(apiUrl, resource);
    const url = `${baseUrl}?${query}`;

    const headers = await getAuthHeaders();
    const response = await authFetch(url, {
      headers: headers,
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    return {
      data: data.results || data,
    };
  },

  createMany: async ({ resource, variables }) => {
    const results = [];

    for (const record of variables) {
      const url = buildResourceUrl(apiUrl, resource);
      
      const headers = await getAuthHeaders();
      const response = await authFetch(url, {
        method: "POST",
        headers: {
          ...headers,
          "Idempotency-Key": crypto.randomUUID(),
        },
        credentials: 'include',
        body: JSON.stringify(record),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      results.push(data);
    }

    return {
      data: results,
    };
  },

  updateMany: async ({ resource, ids, variables }) => {
    const results = [];

    for (const id of ids) {
      const baseUrl = buildResourceUrl(apiUrl, resource);
      const url = `${baseUrl}${id}/`;
      
      const headers = await getAuthHeaders();
      const response = await authFetch(url, {
        method: "PATCH",
        headers: {
          ...headers,
          "Idempotency-Key": crypto.randomUUID(),
        },
        credentials: 'include',
        body: JSON.stringify(variables),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      results.push(data);
    }

    return {
      data: results,
    };
  },

  deleteMany: async ({ resource, ids }) => {
    for (const id of ids) {
      const baseUrl = buildResourceUrl(apiUrl, resource);
      const url = `${baseUrl}${id}/`;
      
      const headers = await getAuthHeaders();
      const response = await authFetch(url, {
        method: "DELETE",
        headers: headers,
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    }

    return {
      data: ids.map((id: any) => ({ id } as any)),
    };
  },

  custom: async ({ url, method, filters, sorters, payload, query, headers }) => {
    // Support passing relative paths like "/v1/core/companies/statistics/" or short paths like "core/companies/statistics/"
    const baseApiUrl = apiUrl || 'http://localhost:8000';
    let normalizedUrl = url;

    // If no scheme, treat as relative
    const isAbsolute = /^https?:\/\//i.test(url);
    if (!isAbsolute) {
      if (url.startsWith('/')) {
        normalizedUrl = `${baseApiUrl}${url}`; // url already has leading slash
      } else {
        normalizedUrl = `${baseApiUrl}/${url}`; // ensure single slash
      }
    }

    // Preserve existing query if present
    const hasQuery = normalizedUrl.includes('?');
    let requestUrl = hasQuery ? normalizedUrl + '&' : normalizedUrl + '?';

    if (sorters && sorters.length > 0) {
      const sortQuery = buildSorters(sorters);
      requestUrl = `${requestUrl}&${queryString.stringify(sortQuery)}`;
    }

    if (filters && filters.length > 0) {
      const filterQuery = buildFilters(filters);
      requestUrl = `${requestUrl}&${queryString.stringify(filterQuery)}`;
    }

    if (query) {
      requestUrl = `${requestUrl}&${queryString.stringify(query)}`;
    }

    try {
      const authHeaders = await getAuthHeaders();
      const response = await authFetch(requestUrl, {
        method: method || "GET",
        headers: {
          ...authHeaders,
          ...headers,
        },
        body: payload ? JSON.stringify(payload) : undefined,
      });

      // Handle authentication errors
      if (response.status === 401) {
        // Avoid aggressive token purging if this was an incidental unauthorized custom call.
        // Let jwtAuthService's internal refresh logic handle lifecycle; only throw to surface to hook.
        console.warn(`Unauthorized (401) for custom URL: ${normalizedUrl}`);
        throw new Error("Authentication required");
      }

      // Check content type before parsing
      const contentType = response.headers.get("content-type");
  console.log(`Content-Type for ${normalizedUrl}:`, contentType, 'Status:', response.status);
      if (!contentType || !contentType.includes("application/json")) {
        console.error(`Non-JSON response for ${normalizedUrl}:`, response.status, response.statusText);
        throw new Error(`Server returned non-JSON response: ${response.status}`);
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: `HTTP ${response.status}` }));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      return {
        data,
      };
    } catch (error) {
      console.error(`Custom API call error for ${normalizedUrl}:`, error);
      throw error; // Re-throw for useCustom to handle
    }
  },
});
